import React, { useCallback, useMemo } from 'react';
import { ClearSaleScoreItem } from '~/typings/entities/person';
import moment from 'moment';
import { useTheme } from 'styled-components';
import { getReliability } from '~/common/utils/clearSale';
import formatScores, { ScoreHistoryItem, ScoreSteps, ScoreStepTitle } from '~/common/formatScores';

import { Timeline, TimelineDate, TimelineItem, TimelineText, TimelineTitle } from './ClearSaleScore.styled';

export type ClearSaleHistoryProps = {
  scoreHistory: ClearSaleScoreItem[];
};

export function ClearSaleHistory({ scoreHistory }: ClearSaleHistoryProps) {
  const theme = useTheme();

  const formattedHistory: ScoreHistoryItem[] = useMemo(() => formatScores(scoreHistory), [scoreHistory]);

  const formatData = (date: string, withoutHours = false) => {
    if (date) {
      const format = withoutHours ? 'DD [de] MMM, YYYY' : 'DD [de] MMM, YYYY [às] HH:mm[h]';
      return moment(date).format(format);
    }

    return '-';
  };

  const renderReliabilityText = (item: ScoreHistoryItem) => {
    const reliability = getReliability(item.score);
    return item.text || reliability?.label;
  };

  const getReliabilityColor = useCallback(
    (scoreItem: ScoreHistoryItem) => {
      if (scoreItem.step === ScoreSteps.tokenSent) {
        return theme.colors.brand.primaryAlt;
      }

      if ([ScoreSteps.emailValidated, ScoreSteps.smsValidated, ScoreSteps.whatsappValidated].includes(scoreItem.step)) {
        return theme.colors.info.primary;
      }

      const reliability = getReliability(scoreItem.score);

      return reliability?.color || theme.colors.element.secondary;
    },
    [getReliability, theme],
  );

  return (
    <Timeline>
      {formattedHistory?.map((item) => (
        <TimelineItem key={`${item.date}_${item.score}`} color={getReliabilityColor(item)}>
          <TimelineTitle>{ScoreStepTitle?.[item.step]}</TimelineTitle>
          <TimelineText>
            {renderReliabilityText(item)}
            {item.score && (
              <>
                {' '}
                - <b>{item.score}%</b>
              </>
            )}
          </TimelineText>
          {item.date && <TimelineDate>{formatData(item.date, item.step === ScoreSteps.initial)}</TimelineDate>}
        </TimelineItem>
      ))}
    </Timeline>
  );
}
